import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Paper, IconButton, Chip,
  TextField, InputAdornment, CircularProgress,
  Divider, Select, MenuItem, FormControl,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import MapIcon from '@mui/icons-material/Map';
import TableRowsIcon from '@mui/icons-material/TableRows';
import HubIcon from '@mui/icons-material/Hub';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LagbasLogo from '../../../../components/LagbasLogo';
import HeroBg from '../components/HeroBg';
import { db } from '../../../../firebase/config';
import {
  collection, getDocs, doc, getDoc, query, orderBy, onSnapshot, updateDoc, Timestamp,
} from 'firebase/firestore';
import Fuse from 'fuse.js';
import { Toaster } from 'sonner';
import {
  MAPPN_ACCENT, STATUS_OPTIONS, IMPL_OPTIONS, TECH_TYPES,
} from '../constants';
import { APPLAB_ACCENT } from '../../constants';
import ComponentDetail from '../components/ComponentDetail';

function StatusDot({ status }) {
  const s = STATUS_OPTIONS.find(x => x.key === status);
  if (!s) return null;
  return (
    <Chip label={s.label} size="small" sx={{
      bgcolor: s.color + '22', color: s.color, fontWeight: 700,
      fontSize: '0.65rem', height: 18, border: `1px solid ${s.color}44`,
    }} />
  );
}

export default function MappNCards() {
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [caseData, setCaseData] = useState(null);
  const [perspectives, setPerspectives] = useState([]);
  const [categories, setCategories] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterPersp, setFilterPersp] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showTech, setShowTech] = useState(false);
  const [expandedCards, setExpandedCards] = useState(() => new Set());

  function toggleExpand(compId, e) {
    e.stopPropagation();
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(compId)) next.delete(compId); else next.add(compId);
      return next;
    });
  }

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [detailData, setDetailData] = useState(null);

  useEffect(() => {
    let unsub;
    async function init() {
      setLoading(true);
      const [cSnap, pSnap, catSnap] = await Promise.all([
        getDoc(doc(db, 'mappnCases', caseId)),
        getDocs(query(collection(db, 'mappnCases', caseId, 'perspectives'), orderBy('order'))),
        getDocs(query(collection(db, 'mappnCases', caseId, 'categories'), orderBy('order'))),
      ]);
      if (!cSnap.exists()) { navigate('/applab/mappn'); return; }
      setCaseData({ id: cSnap.id, ...cSnap.data() });
      setPerspectives(pSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      unsub = onSnapshot(
        query(collection(db, 'mappnCases', caseId, 'components'), orderBy('createdAt', 'desc')),
        snap => {
          setComponents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false);
        }
      );
    }
    init();
    return () => unsub?.();
  }, [caseId, navigate]);

  // Fuse search
  const fuse = useMemo(() => new Fuse(components, {
    keys: ['title', 'description', 'notes'],
    threshold: 0.35,
  }), [components]);

  const filtered = useMemo(() => {
    let list = search ? fuse.search(search).map(r => r.item) : components;
    if (filterPersp) list = list.filter(c => (c.perspectiveIds || []).includes(filterPersp));
    if (filterCat)   list = list.filter(c => c.categoryId === filterCat);
    if (filterStatus) list = list.filter(c => c.status === filterStatus);
    return list;
  }, [components, search, filterPersp, filterCat, filterStatus, fuse]);

  function openNew() {
    setDetailId('__new__');
    setDetailData(null);
    setDetailOpen(true);
  }

  function openEdit(comp) {
    setDetailId(comp.id);
    setDetailData(comp);
    setDetailOpen(true);
  }

  function handleSaved(savedId, savedData) {
    setComponents(prev => {
      const exists = prev.find(c => c.id === savedId);
      if (exists) return prev.map(c => c.id === savedId ? { ...c, ...savedData } : c);
      return [{ id: savedId, ...savedData }, ...prev];
    });
    if (detailId === '__new__') {
      setDetailId(savedId);
      setDetailData(savedData);
    }
    updateDoc(doc(db, 'mappnCases', caseId), { updatedAt: Timestamp.now(), componentCount: components.length + (detailId === '__new__' ? 1 : 0) }).catch(() => {});
  }

  function handleDeleted(deletedId) {
    setComponents(prev => prev.filter(c => c.id !== deletedId));
    setDetailOpen(false);
  }

  if (loading && !caseData) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: MAPPN_ACCENT }} />
      </Box>
    );
  }

  const hasHero = !!(caseData?.heroVideoUrl || caseData?.heroImageUrl);

  return (
    <>
    <HeroBg caseData={caseData} />
    <Box sx={{ minHeight: '100vh', bgcolor: hasHero ? 'transparent' : '#0d1117', color: 'white', position: 'relative', zIndex: 1 }}>
      <Toaster richColors position="top-center" />

      {/* Header */}
      <Box sx={{
        bgcolor:        hasHero ? 'rgba(13,17,23,0.72)' : '#161b22',
        backdropFilter: hasHero ? 'blur(22px) saturate(1.3)' : 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        py: 1.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
        <IconButton color="inherit" size="small" onClick={() => navigate('/applab/mappn')}>
          <ArrowBackIcon />
        </IconButton>
        {caseData?.heroImageUrl ? (
          <Box component="img" src={caseData.heroImageUrl}
            sx={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
        ) : (
          <AccountTreeIcon sx={{ color: MAPPN_ACCENT, fontSize: '1.3rem' }} />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{caseData?.name || '…'}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
            {components.length} komponenter
          </Typography>
        </Box>

        {/* Nav tabs */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {[
            { label: 'Kort',          path: 'cards',        icon: <ViewModuleIcon sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Perspektiv',    path: 'perspectives', icon: <ViewColumnIcon sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Karta',         path: 'map',          icon: <MapIcon        sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Tabell',        path: 'table',        icon: <TableRowsIcon  sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Tech',          path: 'tech',         icon: <HubIcon        sx={{ fontSize: '0.9rem' }} /> },
            { label: 'Inställningar', path: 'settings',     icon: <SettingsIcon   sx={{ fontSize: '0.9rem' }} /> },
          ].map(tab => (
            <Button
              key={tab.path}
              startIcon={tab.icon}
              onClick={() => navigate(`/applab/mappn/${caseId}/${tab.path}`)}
              sx={{
                textTransform: 'none', fontSize: '0.8rem',
                color: tab.path === 'cards' ? MAPPN_ACCENT : 'rgba(255,255,255,0.5)',
                bgcolor: tab.path === 'cards' ? MAPPN_ACCENT + '18' : 'transparent',
                borderRadius: 1,
                '&:hover': { bgcolor: MAPPN_ACCENT + '22' },
              }}
            >
              {tab.label}
            </Button>
          ))}
        </Box>

        <Button
          variant="outlined"
          onClick={() => navigate(`/applab/mockup/new?caseId=${caseId}`)}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
            color: APPLAB_ACCENT, borderColor: APPLAB_ACCENT + '55',
            '&:hover': { borderColor: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '11' },
            ml: 0.5,
          }}
        >
          Skapa MockupN →
        </Button>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={openNew}
          sx={{ bgcolor: MAPPN_ACCENT, '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none', fontWeight: 700, ml: 1 }}
        >
          Ny komponent
        </Button>
      </Box>

      {/* Filter bar */}
      <Box sx={{ px: 3, py: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        <TextField
          size="small"
          placeholder="Sök komponenter…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{
            width: 220,
            '& .MuiOutlinedInput-root': {
              color: 'white', bgcolor: 'rgba(255,255,255,0.04)',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              '&.Mui-focused fieldset': { borderColor: MAPPN_ACCENT },
            },
            '& input::placeholder': { color: 'rgba(255,255,255,0.3)' },
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} /></InputAdornment>,
          }}
        />

        <DarkSelect value={filterPersp} onChange={e => setFilterPersp(e.target.value)} label="Perspektiv">
          <MenuItem value=""><em>Alla perspektiv</em></MenuItem>
          {perspectives.map(p => (
            <MenuItem key={p.id} value={p.id} sx={{ fontSize: '0.85rem' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, mr: 1, display: 'inline-block' }} />
              {p.name}
            </MenuItem>
          ))}
        </DarkSelect>

        <DarkSelect value={filterCat} onChange={e => setFilterCat(e.target.value)} label="Kategori">
          <MenuItem value=""><em>Alla kategorier</em></MenuItem>
          {categories.map(c => (
            <MenuItem key={c.id} value={c.id} sx={{ fontSize: '0.85rem' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c.color, mr: 1, display: 'inline-block' }} />
              {c.name}
            </MenuItem>
          ))}
        </DarkSelect>

        <DarkSelect value={filterStatus} onChange={e => setFilterStatus(e.target.value)} label="Status">
          <MenuItem value=""><em>Alla statusar</em></MenuItem>
          {STATUS_OPTIONS.map(s => (
            <MenuItem key={s.key} value={s.key} sx={{ fontSize: '0.85rem' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, mr: 1, display: 'inline-block' }} />
              {s.label}
            </MenuItem>
          ))}
        </DarkSelect>

        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.75 }}>
          <ToggleChip active={showTech} onClick={() => setShowTech(p => !p)} label="Visa tech" />
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ px: 3, py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: MAPPN_ACCENT }} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', mb: 2 }}>
              {components.length === 0 ? 'Inga komponenter ännu' : 'Inga komponenter matchar filtret'}
            </Typography>
            {components.length === 0 && (
              <Button
                variant="contained" startIcon={<AddIcon />}
                onClick={openNew}
                sx={{ bgcolor: MAPPN_ACCENT, textTransform: 'none' }}
              >
                Lägg till första komponenten
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filtered.map(comp => {
              const cat = categories.find(c => c.id === comp.categoryId);
              const compPerspectives = perspectives.filter(p => (comp.perspectiveIds || []).includes(p.id));
              const impl = IMPL_OPTIONS.find(o => o.key === comp.implementation);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={comp.id}>
                  <Paper
                    onClick={() => openEdit(comp)}
                    sx={{
                      bgcolor: '#161b22',
                      border: '1px solid rgba(255,255,255,0.07)',
                      borderRadius: 2, p: 2, cursor: 'pointer',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      '&:hover': { borderColor: MAPPN_ACCENT + '55', boxShadow: `0 0 0 1px ${MAPPN_ACCENT}22` },
                      display: 'flex', flexDirection: 'column', gap: 1,
                      height: '100%',
                    }}
                  >
                    {/* Top row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                      {comp.avatarUrl && (
                        <Box component="img" src={comp.avatarUrl}
                          sx={{ width: 32, height: 32, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={700} sx={{ fontSize: '0.85rem', color: 'white', lineHeight: 1.3 }}>
                          {comp.title || '(Namnlös)'}
                        </Typography>
                        {impl && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.35 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: impl.color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.62rem', color: impl.color, fontWeight: 700, lineHeight: 1 }}>{impl.label}</Typography>
                          </Box>
                        )}
                      </Box>
                      <StatusDot status={comp.status} />
                    </Box>

                    {/* Description */}
                    {comp.description && (
                      <Typography sx={{
                        fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)',
                        lineHeight: 1.5,
                        display: '-webkit-box', WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {comp.description}
                      </Typography>
                    )}

                    {/* Category + perspectives */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 'auto' }}>
                      {cat && (
                        <Chip label={cat.name} size="small" sx={{
                          bgcolor: cat.color + '22', color: cat.color,
                          fontSize: '0.63rem', height: 16, fontWeight: 700,
                        }} />
                      )}
                      {compPerspectives.map(p => (
                        <Chip key={p.id} label={p.name} size="small" sx={{
                          bgcolor: p.color + '18', color: p.color,
                          fontSize: '0.63rem', height: 16,
                        }} />
                      ))}
                    </Box>

                    {/* Footer */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      {(comp.features || []).length > 0 && (
                        <Box
                          onClick={e => toggleExpand(comp.id, e)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.2, cursor: 'pointer', '&:hover': { opacity: 0.75 } }}
                        >
                          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)' }}>
                            {comp.features.length} funk.
                          </Typography>
                          <ExpandMoreIcon sx={{
                            fontSize: '0.8rem', color: 'rgba(255,255,255,0.25)',
                            transition: 'transform 0.15s',
                            transform: expandedCards.has(comp.id) ? 'rotate(180deg)' : 'none',
                          }} />
                        </Box>
                      )}
                      {comp.unresolvedCommentCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <ChatBubbleOutlineIcon sx={{ fontSize: '0.7rem', color: MAPPN_ACCENT + 'aa' }} />
                          <Typography sx={{ fontSize: '0.65rem', color: MAPPN_ACCENT + 'aa' }}>
                            {comp.unresolvedCommentCount}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Tech chips */}
                    {showTech && (comp.techStack || []).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                        {comp.techStack.slice(0, 3).map(t => {
                          const tt = TECH_TYPES.find(x => x.key === t.type) || TECH_TYPES[7];
                          return (
                            <Chip key={t.id} label={t.name} size="small" sx={{
                              bgcolor: tt.bg + '44', color: 'rgba(255,255,255,0.7)',
                              fontSize: '0.6rem', height: 14,
                            }} />
                          );
                        })}
                        {comp.techStack.length > 3 && (
                          <Chip label={`+${comp.techStack.length - 3}`} size="small" sx={{
                            bgcolor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.4)',
                            fontSize: '0.6rem', height: 14,
                          }} />
                        )}
                      </Box>
                    )}

                    {/* Expandable feature list */}
                    {expandedCards.has(comp.id) && (comp.features || []).length > 0 && (
                      <Box onClick={e => e.stopPropagation()} sx={{ pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                        {comp.features.map(f => (
                          <Typography key={f.id} sx={{
                            fontSize: '0.68rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7,
                            pl: 0.75, borderLeft: '1px solid rgba(255,255,255,0.07)',
                          }}>
                            {f.name || '(Namnlös)'}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Paper
                onClick={openNew}
                sx={{
                  bgcolor: 'transparent',
                  border: '1.5px dashed rgba(255,255,255,0.12)',
                  borderRadius: 2, p: 2, cursor: 'pointer',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': { borderColor: MAPPN_ACCENT + '66', bgcolor: MAPPN_ACCENT + '08' },
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  alignItems: 'center', gap: 1, minHeight: 100,
                }}
              >
                <AddIcon sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '1.5rem' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem' }}>
                  Ny komponent
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Detail panel */}
      <ComponentDetail
        open={detailOpen}
        caseId={caseId}
        componentId={detailId}
        initialData={detailData}
        perspectives={perspectives}
        categories={categories}
        caseTechStack={caseData?.techStack || []}
        onClose={() => setDetailOpen(false)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </Box>
    </>
  );
}

function DarkSelect({ value, onChange, label, children }) {
  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <Select
        value={value}
        onChange={onChange}
        displayEmpty
        sx={{
          color: value ? 'white' : 'rgba(255,255,255,0.3)',
          fontSize: '0.82rem',
          bgcolor: 'rgba(255,255,255,0.04)',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: MAPPN_ACCENT },
          '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.3)' },
        }}
      >
        {children}
      </Select>
    </FormControl>
  );
}

function ToggleChip({ active, onClick, label }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        bgcolor: active ? MAPPN_ACCENT + '33' : 'rgba(255,255,255,0.05)',
        color: active ? MAPPN_ACCENT : 'rgba(255,255,255,0.4)',
        border: `1px solid ${active ? MAPPN_ACCENT + '55' : 'transparent'}`,
        fontWeight: active ? 700 : 400,
        fontSize: '0.75rem',
        '&:hover': { bgcolor: MAPPN_ACCENT + '22' },
      }}
    />
  );
}
